import { todayPT } from "@homebound/activesupport";
import { Css } from "@homebound/beam";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import {
  BidContractType,
  SaveBidContractInput,
  SavePlanBasedBidContractLineItemInput,
} from "src/generated/graphqlTypes";
import { createBidPackagesDetailUrl } from "src/RouteUrls";
import { fail, pushInNextLoop, queryResult } from "src/utils";
import { PlanBasedBidsTableV2 } from "./components/PlanBasedBidsTableV2";
import {
  EnterPlanBasedBidPage_BidPackageFragment,
  useEnterPlanBasedBidPage_SaveBidContractMutation,
  useEnterPlanBasedBidPageQuery,
} from "./EnterPlanBasedBidPage.generated";

export function EnterPlanBasedBidPage() {
  const { bidPackageId, bidderId } = useParams<{ bidPackageId: string; bidderId: string }>();
  const query = useEnterPlanBasedBidPageQuery({ variables: { bidPackageId } });
  return queryResult(query, ({ bidPackage }) => (
    <EnterPlanBasedBidPageView bidPackage={bidPackage} bidderId={bidderId} />
  ));
}

type EnterPlanBasedBidPageViewProps = { bidPackage: EnterPlanBasedBidPage_BidPackageFragment; bidderId: string };

function EnterPlanBasedBidPageView(props: EnterPlanBasedBidPageViewProps) {
  const { bidPackage, bidderId } = props;
  const history = useHistory();
  const bidder =
    bidPackage.activeBidders.find((b) => b.id === bidderId) ??
    fail(`Bidder ${bidderId} not found on bid package ${bidPackage.id}`);

  const [saveBidContract] = useEnterPlanBasedBidPage_SaveBidContractMutation();

  const onSave = useCallback(
    async (planBasedLineItems: SavePlanBasedBidContractLineItemInput[]) => {
      // For an existing bid contract, just attach line items via id; the resolver writes them to the latest revision.
      // For a new one, send the BC creation fields - the resolver auto-creates the initial revision in the same call.
      const existingBidContractId = bidder.bidContract?.id;
      const input: SaveBidContractInput = existingBidContractId
        ? { id: existingBidContractId, planBasedLineItems }
        : {
            type: BidContractType.UserCreatedGroupBased,
            tradePartnerId: bidder.tradePartner?.id ?? fail(`Trade-partner bidder ${bidder.id} has no tradePartner`),
            bidRequestId: bidder.request?.id ?? fail(`Trade-partner bidder ${bidder.id} has no request`),
            startDate: todayPT(),
            planBasedLineItems,
          };
      await saveBidContract({ variables: { input } });
      pushInNextLoop(history, createBidPackagesDetailUrl(bidPackage.bidPackageGroup!.id, bidPackage.id));
    },
    [bidder, bidPackage, saveBidContract, history],
  );

  return (
    <>
      <div css={Css.xl2.tac.p2.fs0.$}>Enter Bid Information</div>
      <PlanBasedBidsTableV2 bidPackage={bidPackage} mode="edit" bidderId={bidder.id} onSave={onSave} />
    </>
  );
}
